
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { redirectToSsoProvider } from '@/utils/sso';
import { getRestaurantMetaInfo } from '@/utils/meta';
import LoginSignup from '@/components/auth/LoginSignup.vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';

const namespace: string = 'restaurant';

@Component<Login>({
	components: {
		LoginSignup,
		LoadingSpinner
	},
	metaInfo() {
		return getRestaurantMetaInfo(this.restaurant);
	}
})
export default class Login extends Vue {
	@Action('fetchRestaurant', { namespace }) private fetchRestaurant!: (payload: FetchRestaurantPayload) => Promise<boolean>;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('hasCssLoaded', { namespace }) private cssLoaded!: boolean;
	@Getter('getRestaurantSso', { namespace }) private restaurantSso!: Sso | null;

	private dataFetched: boolean = true;
	private redirectingToSso: boolean = false;

	private loginCredentials: LoginCredentials = {
		email: '',
		password: ''
	};

	private async created(): Promise<void> {
		// Set and remove credentials from sessionStorage
		const storedCredentials = sessionStorage.getItem('tempAuthCredentials');
		if (storedCredentials) {
			this.loginCredentials = JSON.parse(storedCredentials || '{}');
			sessionStorage.removeItem('tempAuthCredentials');
		}

		if (!this.restaurant?.name) {
			const restaurantSlug: string | null = sessionStorage.getItem('restaurantSlug');

			if (restaurantSlug) {
				this.dataFetched = false;
				try {
					await this.fetchRestaurant({
						restaurantSlug,
						route: this.$route,
						router: this.$router
					});
				}
				catch (error) {
					if (error.errorMessage) {
						this.$toasted.show(error.errorMessage, { type: 'error', position: 'top-center' }).goAway(5000);
					}
				}
				finally {
					this.dataFetched = true;
				}
			}
		}

		// An SSO location never shows a login form, it hands off to its provider straight away
		if (this.restaurantSso) {
			await this.startSsoFlow();
		}
	}

	/**
	 * Hand off to the location's SSO provider, asking for a silent re-authentication so the user
	 * never sees a login screen when they already have a session with the provider.
	 *
	 * @return {Promise<void>}
	 */
	private async startSsoFlow(): Promise<void> {
		this.redirectingToSso = true;

		try {
			await redirectToSsoProvider(this.restaurantSso!, this.restaurant.slug, this.$route.query, true);
		}
		catch (error) {
			this.redirectingToSso = false;
			this.$router.replace({ name: 'AccessDenied' }).catch(() => {});
		}
	}

	private backToMenu(): void {
		this.$router.push({ path: `/${this.restaurant.slug}`, query: this.$route.query, hash: this.$route.hash }).catch(() => {});
	}
}
